import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Music, ArrowLeft, ChevronRight, Check, Loader2, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 8; // 7 quiz + 1 checkout
const MAX_QUALITIES_LENGTH = 1280;
const MAX_MEMORIES_LENGTH = 2000;
const MAX_MESSAGE_LENGTH = 1000;

const RECIPIENTS = ["Husband", "Wife", "Children", "Father", "Mother", "Sibling", "Friend", "Myself", "Other"];
const OCCASIONS = ["Anniversary", "Birthday", "Wedding", "Memorial / In Memory", "Mother's Day / Father's Day", "Valentine's Day", "Holiday / Christmas", "Just Because", "Healing / Strength", "Other"];
const GENRES = ["Pop", "Country", "Rock", "R&B", "Jazz", "Worship", "Rap / Hip-Hop", "Acoustic/Folk", "Classical/Piano", "Latin", "Electronic"];
const VOICES = ["Female Voice", "Male Voice", "No Preference"];
const TONES = ["Joyful / Celebratory", "Romantic / Tender", "Reflective / Nostalgic", "Heartfelt / Emotional", "Uplifting / Hopeful", "Bittersweet", "Fun / Playful", "Powerful / Anthemic"];
const LANGUAGES = ["English", "Spanish", "French", "Portuguese", "German", "Italian", "Korean", "Japanese", "Chinese", "Hindi", "Arabic", "Other"];

const OCCASION_TESTIMONIALS = {
  "Anniversary": { text: "40 years of marriage and he said it was the best gift ever.", author: "Margaret T." },
  "Birthday": { text: "She plays it every morning. The only gift that truly captured how she feels.", author: "Robert H." },
  "Memorial / In Memory": { text: "My whole family was in tears. It was perfect.", author: "David K." },
  "Wedding": { text: "We used it as our first dance song. Everyone was crying.", author: "Jennifer L." },
};

export default function QuizFunnel() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [quiz, setQuiz] = useState(() => {
    const saved = localStorage.getItem("storytosong_quiz");
    return saved ? JSON.parse(saved) : {
      recipient: "", recipient_name: "", pronunciation: "", relationship_other: "",
      occasion: "", genre: "", voice_gender: "", tones: [],
      qualities: "", memories: "", message: "",
      language: "English", email: "", phone: "", rush_delivery: false,
    };
  });

  useEffect(() => {
    localStorage.setItem("storytosong_quiz", JSON.stringify(quiz));
  }, [quiz]);

  const update = useCallback((key, val) => {
    setQuiz(prev => ({ ...prev, [key]: val }));
  }, []);

  const progress = (step / TOTAL_STEPS) * 100;
  const isSelf = quiz.recipient === "Myself";

  const canNext = () => {
    switch (step) {
      case 1: return quiz.recipient && quiz.recipient_name;
      case 2: return quiz.occasion;
      case 3: return quiz.genre && quiz.voice_gender;
      case 4: return quiz.tones.length > 0;
      case 5: return quiz.qualities.length >= 10;
      case 6: return quiz.memories.length >= 10;
      case 7: return true; // message is optional
      case 8: return quiz.email;
      default: return false;
    }
  };

  const nextStep = () => {
    if (step < TOTAL_STEPS) setStep(step + 1);
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    else navigate("/");
  };

  const toggleTone = (tone) => {
    const current = quiz.tones;
    if (current.includes(tone)) {
      update("tones", current.filter(t => t !== tone));
    } else if (current.length < 3) {
      update("tones", [...current, tone]);
    }
  };

  const submitOrder = async () => {
    if (!quiz.email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      // Submit quiz
      const voiceMap = { "Female Voice": "f", "Male Voice": "m", "No Preference": "any" };
      const payload = {
        ...quiz,
        voice_gender: voiceMap[quiz.voice_gender] || "any",
      };
      const { data: orderData } = await axios.post(`${API}/quiz`, payload);

      // Create checkout
      const origin = window.location.origin;
      const { data: checkoutData } = await axios.post(`${API}/checkout/create`, {
        order_id: orderData.order_id,
        origin_url: origin,
      });

      // Clear saved quiz
      localStorage.removeItem("storytosong_quiz");

      // Redirect to Stripe
      window.location.href = checkoutData.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong";
      console.error("Checkout error:", err?.response?.status, msg, err);
      toast.error(`Checkout failed: ${msg}. Please try again.`);
      setLoading(false);
    }
  };

  const appendToField = (field, text) => {
    const current = quiz[field];
    const newVal = current ? `${current} ${text}` : text;
    update(field, newVal);
  };

  const renderStep = () => {
    switch (step) {
      case 1: return (
        <div data-testid="quiz-step-1" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">Tell us about the special person in your life</h2>
            <p className="text-[#5A5A5A] text-sm">Who&apos;s this for?</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {RECIPIENTS.map(r => (
              <button key={r} data-testid={`recipient-${r}`}
                onClick={() => { update("recipient", r); if (r === "Myself") update("recipient_name", "Me"); }}
                className={`quiz-option text-center font-medium ${quiz.recipient === r ? "selected" : ""}`}>
                {r}
              </button>
            ))}
          </div>
          {quiz.recipient === "Other" && (
            <Input data-testid="relationship-other-input" placeholder="What's your relationship?"
              value={quiz.relationship_other} onChange={e => update("relationship_other", e.target.value)}
              className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3" />
          )}
          {quiz.recipient && quiz.recipient !== "Myself" && (
            <div className="space-y-3">
              <label className="text-[#5A5A5A] font-bold text-sm block">What&apos;s their name?</label>
              <Input data-testid="recipient-name-input" placeholder="Enter their first name"
                value={quiz.recipient_name} onChange={e => update("recipient_name", e.target.value)}
                className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3" />
              <p className="text-xs text-[#5A5A5A]">
                Tip: add pronunciation for clarity (e.g. Alicia: ah-lee-sha)
              </p>
              <Input data-testid="pronunciation-input" placeholder="Pronunciation (optional)"
                value={quiz.pronunciation} onChange={e => update("pronunciation", e.target.value)}
                className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3" />
            </div>
          )}
        </div>
      );

      case 2: return (
        <div data-testid="quiz-step-2" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">What&apos;s the occasion?</h2>
            <p className="text-[#5A5A5A] text-sm">This helps us set the right tone for your song.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {OCCASIONS.map(o => (
              <button key={o} data-testid={`occasion-${o}`}
                onClick={() => update("occasion", o)}
                className={`quiz-option font-medium ${quiz.occasion === o ? "selected" : ""}`}>
                {o}
              </button>
            ))}
          </div>
          {quiz.occasion && OCCASION_TESTIMONIALS[quiz.occasion] && (
            <div className="bg-[#F9EDED] rounded-xl p-4 text-sm text-[#5A5A5A] italic">
              &ldquo;{OCCASION_TESTIMONIALS[quiz.occasion].text}&rdquo;
              <span className="block mt-1 not-italic font-bold text-[#7D1B1B]">— {OCCASION_TESTIMONIALS[quiz.occasion].author}</span>
            </div>
          )}
        </div>
      );

      case 3: return (
        <div data-testid="quiz-step-3" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">Choose a Genre</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {GENRES.map(g => (
              <button key={g} data-testid={`genre-${g}`}
                onClick={() => update("genre", g)}
                className={`quiz-option text-center font-medium ${quiz.genre === g ? "selected" : ""}`}>
                {g}
              </button>
            ))}
          </div>
          <div className="pt-4">
            <label className="text-[#5A5A5A] font-bold text-sm block mb-2">Preferred Voice Gender</label>
            <p className="text-xs text-[#5A5A5A] mb-3">We recommend choosing your own gender so the vocals feel personal.</p>
            <div className="grid grid-cols-3 gap-3">
              {VOICES.map(v => (
                <button key={v} data-testid={`voice-${v}`}
                  onClick={() => update("voice_gender", v)}
                  className={`quiz-option text-center font-medium text-sm ${quiz.voice_gender === v ? "selected" : ""}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      );

      case 4: return (
        <div data-testid="quiz-step-4" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">What feeling should this song capture?</h2>
            <p className="text-[#5A5A5A] text-sm">Pick up to 3 that feel right.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {TONES.map(t => (
              <button key={t} data-testid={`tone-${t}`}
                onClick={() => toggleTone(t)}
                className={`tone-chip ${quiz.tones.includes(t) ? "selected" : ""}`}>
                {quiz.tones.includes(t) && <Check className="inline h-3.5 w-3.5 mr-1" />}
                {t}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#5A5A5A]">{quiz.tones.length}/3 selected</p>
        </div>
      );

      case 5: return (
        <div data-testid="quiz-step-5" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">
              What makes {isSelf ? "you" : "them"} special?
            </h2>
            <p className="text-[#5A5A5A] text-sm">
              Describe {isSelf ? "your" : "their"} character and the qualities you love most.
            </p>
          </div>
          <Textarea data-testid="qualities-textarea"
            value={quiz.qualities} onChange={e => update("qualities", e.target.value)}
            maxLength={MAX_QUALITIES_LENGTH}
            placeholder={`Are ${isSelf ? "you" : "they"} patient, wise, funny, protective, encouraging? What makes ${isSelf ? "you" : "them"} the incredible person ${isSelf ? "you are" : "they are"}?`}
            className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3 min-h-[150px] resize-y" />
          <p className="text-xs text-[#5A5A5A] text-right">{quiz.qualities.length}/{MAX_QUALITIES_LENGTH.toLocaleString()}</p>
          <div className="flex flex-wrap gap-2">
            {[
              "What's the first word that comes to mind?",
              "What do they do that always makes you smile?",
              "What would the world lose without them?"
            ].map((prompt, i) => (
              <button key={i} data-testid={`quality-prompt-${i}`}
                onClick={() => appendToField("qualities", prompt)}
                className="text-xs bg-[#F9EDED] text-[#7D1B1B] px-3 py-1.5 rounded-full hover:bg-[#7D1B1B] hover:text-white transition-colors">
                + {prompt}
              </button>
            ))}
          </div>
        </div>
      );

      case 6: return (
        <div data-testid="quiz-step-6" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">Share your favorite memories</h2>
            <p className="text-[#5A5A5A] text-sm">What moments do you treasure most?</p>
          </div>
          <Textarea data-testid="memories-textarea"
            value={quiz.memories} onChange={e => update("memories", e.target.value)}
            maxLength={MAX_MEMORIES_LENGTH}
            placeholder="Share how you met, special years, inside jokes, or moments that made your relationship unique..."
            className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3 min-h-[180px] resize-y" />
          <p className="text-xs text-[#5A5A5A]">
            <span className="text-right block">{quiz.memories.length}/{MAX_MEMORIES_LENGTH.toLocaleString()}</span>
            The more detail you share, the more personal your song will be.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              "How did you meet?",
              "What's a trip or adventure you shared?",
              "Is there a phrase they always say?",
              "What's a moment you'll never forget?"
            ].map((prompt, i) => (
              <button key={i} data-testid={`memory-prompt-${i}`}
                onClick={() => appendToField("memories", prompt)}
                className="text-xs bg-[#F9EDED] text-[#7D1B1B] px-3 py-1.5 rounded-full hover:bg-[#7D1B1B] hover:text-white transition-colors">
                + {prompt}
              </button>
            ))}
          </div>
        </div>
      );

      case 7: return (
        <div data-testid="quiz-step-7" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">A message from your heart</h2>
            <p className="text-[#5A5A5A] text-sm">
              Write anything else you feel would be relevant. <span className="italic">This is optional.</span>
            </p>
          </div>
          <Textarea data-testid="message-textarea"
            value={quiz.message} onChange={e => update("message", e.target.value)}
            maxLength={MAX_MESSAGE_LENGTH}
            placeholder="What do you want them to know, feel, or remember?"
            className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3 min-h-[150px] resize-y" />
          <p className="text-xs text-[#5A5A5A] text-right">{quiz.message.length}/{MAX_MESSAGE_LENGTH.toLocaleString()}</p>
          <p className="text-xs text-[#5A5A5A] italic">If you skip it, our songwriter will craft the perfect words from your story.</p>
        </div>
      );

      case 8: return (
        <div data-testid="quiz-step-8" className="space-y-6 animate-fade-in">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">Almost There!</h2>
            <p className="text-[#5A5A5A] text-sm">
              You&apos;re just one click away from creating a beautiful song for {quiz.recipient_name || "someone special"}.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[#5A5A5A] font-bold text-sm block mb-2">Your Song Language</label>
              <Select value={quiz.language} onValueChange={val => update("language", val)}>
                <SelectTrigger data-testid="language-select" className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3 h-auto">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[#5A5A5A] font-bold text-sm block mb-2">Your Email Address</label>
              <Input data-testid="email-input" type="email" placeholder="you@email.com"
                value={quiz.email} onChange={e => update("email", e.target.value)}
                className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3" />
            </div>
            <div>
              <label className="text-[#5A5A5A] font-bold text-sm block mb-2">Your Phone Number <span className="font-normal">(optional)</span></label>
              <Input data-testid="phone-input" type="tel" placeholder="+1 (555) 000-0000"
                value={quiz.phone} onChange={e => update("phone", e.target.value)}
                className="bg-white border-2 border-[#E0E0E0] focus:border-[#7D1B1B] rounded-xl px-4 py-3" />
              <p className="text-xs text-[#5A5A5A] mt-1">We&apos;ll send you a text when your song is ready.</p>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-2xl p-5 border border-[#E0E0E0] space-y-3">
            <h3 className="font-bold text-[#2C2C2C] text-sm">Order Summary</h3>
            <div className="grid grid-cols-2 gap-2 text-sm text-[#5A5A5A]">
              <span>Recipient:</span><span className="font-medium text-[#2C2C2C]">{quiz.recipient_name}</span>
              <span>Relationship:</span><span className="font-medium text-[#2C2C2C]">{quiz.recipient}</span>
              <span>Occasion:</span><span className="font-medium text-[#2C2C2C]">{quiz.occasion}</span>
              <span>Genre:</span><span className="font-medium text-[#2C2C2C]">{quiz.genre}</span>
              <span>Voice:</span><span className="font-medium text-[#2C2C2C]">{quiz.voice_gender}</span>
              <span>Tones:</span><span className="font-medium text-[#2C2C2C]">{quiz.tones.join(", ")}</span>
            </div>
            <div className="border-t border-[#E0E0E0] pt-3 flex items-center justify-between">
              <span className="font-bold text-[#2C2C2C]">Custom Song</span>
              <span className="font-bold text-[#2C2C2C]">$69.00</span>
            </div>
            <p data-testid="delivery-speed-note" className="text-xs text-[#7D1B1B] font-medium flex items-center gap-1.5 -mt-1">
              <Clock className="h-3.5 w-3.5" /> Receive it in just two business days!
            </p>
            {/* Rush delivery toggle */}
            <label className="flex items-center gap-3 p-3 bg-[#F9EDED] rounded-xl cursor-pointer">
              <input data-testid="rush-delivery-checkbox" type="checkbox" checked={quiz.rush_delivery}
                onChange={e => update("rush_delivery", e.target.checked)}
                className="w-5 h-5 accent-[#7D1B1B]" />
              <div className="flex-1">
                <span className="font-bold text-sm text-[#2C2C2C]">Rush Delivery</span>
                <p className="text-xs text-[#5A5A5A]">Song ready within 24 hours</p>
              </div>
              <span className="font-bold text-[#7D1B1B]">+$30</span>
            </label>
            <div className="flex items-center justify-between text-lg font-bold">
              <span>Total</span>
              <span className="text-[#7D1B1B]">${quiz.rush_delivery ? "99.00" : "69.00"}</span>
            </div>
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#F5F5F5]/95 backdrop-blur-md border-b border-[#E0E0E0]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button data-testid="quiz-back-btn" onClick={prevStep} className="flex items-center gap-1 text-[#5A5A5A] hover:text-[#2C2C2C] transition-colors">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-[#7D1B1B]" strokeWidth={1.5} />
            <span className="font-display text-lg font-bold text-[#2C2C2C]">StorytoSong</span>
          </div>
          <span className="text-sm text-[#5A5A5A] font-medium">{step}/{TOTAL_STEPS}</span>
        </div>
        <div className="px-4 pb-3 max-w-2xl mx-auto">
          <Progress value={progress} className="h-2 bg-[#E0E0E0]"
            style={{ "--tw-bg-opacity": 1 }} />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {renderStep()}

        {/* Navigation */}
        <div className="mt-8 flex justify-end">
          {step < TOTAL_STEPS ? (
            <button data-testid="quiz-next-btn" onClick={nextStep} disabled={!canNext()}
              className={`rounded-full px-8 py-3.5 font-bold text-base transition-all transform hover:-translate-y-0.5 shadow-md flex items-center gap-2
                ${canNext() ? "bg-[#7D1B1B] text-white hover:bg-[#9B2C2C]" : "bg-[#E0E0E0] text-[#888888] cursor-not-allowed"}`}>
              Next <ChevronRight className="h-5 w-5" />
            </button>
          ) : (
            <button data-testid="checkout-btn" onClick={submitOrder} disabled={!canNext() || loading}
              className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-3.5 font-bold text-base shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Processing...</> : <>Continue to Checkout <ChevronRight className="h-5 w-5" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
